// Teacher-only learner-work operations live behind the learner gateway. The
// signed-in adult's short-lived Directus token proves identity to the gateway;
// learner records and the gateway service credential never reach this app.
import { GATEWAY_URL, IS_PREVIEW } from "../config";
import directus from "./directusClient";

export class TeacherWorkError extends Error {
  constructor(status, reason) {
    super(reason || `gateway answered ${status}`);
    this.response = { status };
    this.reason = reason || null;
  }
}

export const isTeacherWorkAvailable = () => !IS_PREVIEW && Boolean(GATEWAY_URL);

const call = async (path, { method = "GET", json, blob = false } = {}) => {
  const token = await directus.getToken();
  if (!token) throw new TeacherWorkError(401, "not-signed-in");
  const response = await fetch(`${GATEWAY_URL}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: json !== undefined ? JSON.stringify(json) : undefined,
  });
  if (blob && response.ok) return response.blob();
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new TeacherWorkError(response.status, data?.reason);
  return data;
};

export const createActivityPreview = ({ classroomId, activityId }) =>
  call("/teacher/previews", { method: "POST", json: { classroomId, activityId } });

export const readActivityProgress = ({ classroomId, deliverySyncKey, activityId }) => {
  const query = new URLSearchParams({
    classroom: classroomId,
    delivery: deliverySyncKey,
    activity: activityId,
  });
  return call(`/teacher/activity-progress?${query}`);
};

export const readLearnerWork = (attemptId) => call(`/teacher/attempts/${attemptId}`);

export const saveLearnerReview = (attemptId, review) =>
  call(`/teacher/attempts/${attemptId}/review`, { method: "PUT", json: review });

export const readEvidenceBlob = (evidenceId) =>
  call(`/teacher/evidence/${evidenceId}`, { blob: true });

// Handoff 006 classroom workspace. Every route is scoped by the gateway to the
// signed-in adult's classes; nothing here decides who may see what.
const classroomPath = (classroomId) =>
  `/teacher/classrooms/${encodeURIComponent(classroomId)}`;
const learnerPath = (classroomId, learnerId) =>
  `${classroomPath(classroomId)}/learners/${encodeURIComponent(learnerId)}`;

export const readClassroomOverview = (classroomId) =>
  call(`${classroomPath(classroomId)}/overview`);

export const readClassroomLearners = (classroomId) =>
  call(`${classroomPath(classroomId)}/learners`);

export const readReviewQueue = (classroomId, { limit = 25, offset = 0 } = {}) =>
  call(`${classroomPath(classroomId)}/work?${new URLSearchParams({ limit, offset })}`);

export const readLearnerDetail = (classroomId, learnerId) =>
  call(learnerPath(classroomId, learnerId));

export const readLearnerObservations = (classroomId, learnerId) =>
  call(`${learnerPath(classroomId, learnerId)}/observations`);

// Retrying with the same operationId returns the same observation.
export const saveLearnerObservation = (classroomId, learnerId, observation) =>
  call(`${learnerPath(classroomId, learnerId)}/observations`, {
    method: "POST",
    json: observation,
  }).then((data) => data.observation);

// Handoff 007 asynchronous assignments. The gateway re-authorizes the adult
// for the classroom, checks the activity is approved and active, checks every
// selected learner is actively enrolled, and snapshots the response policy.
const assignmentsPath = (classroomId) => `${classroomPath(classroomId)}/assignments`;

export const readAssignments = (classroomId) =>
  call(assignmentsPath(classroomId)).then((data) => data.assignments || []);

// Sending again with the same operationId returns the same assignment.
export const createAssignment = (classroomId, assignment) =>
  call(assignmentsPath(classroomId), { method: "POST", json: assignment }).then(
    (data) => data.assignment
  );

export const readAssignment = (classroomId, assignmentId) =>
  call(`${assignmentsPath(classroomId)}/${encodeURIComponent(assignmentId)}`);

// Stops new starts; learners who began keep their work.
export const closeAssignment = (classroomId, assignmentId) =>
  call(`${assignmentsPath(classroomId)}/${encodeURIComponent(assignmentId)}/close`, {
    method: "POST",
    json: {},
  }).then((data) => data.assignment);
